import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { authStore } from "entities/Auth";
import { createRailSearchQueryParams, railSearchCardStore } from "entities/RailSearchCard";
import { searchFilterStore } from "entities/SearchFilter";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";

import { railSearchValueValidator } from "../utils";
import { useRailSearch } from "./railSearch.hook";

type UseRailRequestStrategyReturnType = {
  handleSearch: (reference: string) => void;
};

type UseRailRequestStrategyParams = {
  isReferenceEqualToOpenedCard: (reference: string) => boolean;
  searchValue: string;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  openCard: (card: ShipmentModel) => void;
  showMainView: () => void;
  showIntroView: () => void;
  clearViewHistory: () => void;
};

export const useRailRequestStrategy = ({
  isReferenceEqualToOpenedCard,
  searchValue,
  setErrorMessage,
  openCard,
  showMainView,
  showIntroView,
  clearViewHistory,
}: UseRailRequestStrategyParams): UseRailRequestStrategyReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  // const savedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const searchCards = useAppSelector(railSearchCardStore.selectors.searchCards);
  const carrier = useAppSelector(searchFilterStore.selectors.carrier);
  const { railSearchRequest, isRailSearchLoading } = useRailSearch({ isAuthenticated });

  const validatorMeta = useRef<ReturnType<typeof railSearchValueValidator.validate>>(
    railSearchValueValidator.validate(searchValue)
  );

  useEffect(() => {
    validatorMeta.current = railSearchValueValidator.validate(searchValue);
  }, [searchValue]);

  const getRequestFuncByStrategy = (
    strategy: "authenticated" | "not-authenticated",
    reference: string
  ): (() => void) | null => {
    const carrierScacCode = carrier?.scacCodes[0];
    const reqParams = createRailSearchQueryParams({
      requestType: "search",
      override: {
        number: reference,
        carrier_code: carrierScacCode,
      },
    });
    if (!reqParams) return null;

    switch (strategy) {
      case "authenticated":
        return () => {
          // railSearchRequest(
          //   { where: { reference } },
          //   {
          //     onSettled: (response) => {
          //       if (response?.length) {
          //         const data = response[0];
          //         dispatch(railSavedCardStore.actions.prependSavedCardAction({ data }));
          //         if (data?.reference) {
          //           // scrollToElement(data.reference);
          //           openCard(data);
          //         }
          //       } else {
          //         // const dlm = new DataLayersManager().searchInitiated();
          //         railSearchRequest(reqParams, {
          //           onSuccess: (response) => {
          //             if (response.requestLimitReached) return;
          //             // dlm.setModel(response).handleSearch();
          //             dispatch(railSearchCardStore.actions.prependSearchCardAction({ data: response }));
          //             if (response?.reference) {
          //               // scrollToElement(response.reference);
          //               openCard(response);
          //             }
          //           },
          //         });
          //       }
          //     },
          //   }
          // );
          railSearchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (response) => {
              if (response.requestLimitReached) return;

              // dlm.setModel(response).handleSearch();
              dispatch(railSearchCardStore.actions.prependSearchCardAction({ data: response }));
              if (response?.reference) {
                // scrollToElement(response.reference);
                if (response.isSuccess) {
                  openCard(response);
                }
              }
            },
          });
        };
      case "not-authenticated":
      default:
        return () => {
          // const dlm = new DataLayersManager().searchInitiated();
          railSearchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (response) => {
              if (response.requestLimitReached) return;

              // dlm.setModel(response).handleSearch();
              dispatch(railSearchCardStore.actions.prependSearchCardAction({ data: response }));
              if (response?.reference) {
                // scrollToElement(response.reference);
                if (response.isSuccess) {
                  openCard(response);
                }
              }
            },
          });
        };
    }
  };

  /**
   * 1. find existing search card OR
   * 2. if not authenticated - block search OR
   * 3. find existing user card OR
   * 4. search user card in DB OR
   * 5. run search and prepend search card to search cards list
   */
  const performRequest = async (reference: string) => {
    const targetSearchCard = searchCards.find((card) => card.reference === reference);
    if (targetSearchCard) {
      if (targetSearchCard?.reference && targetSearchCard.isSuccess) {
        openCard(targetSearchCard);
      }
      return;
    }

    // const targetSavedCard = savedCards.find((card) => card.reference === reference);
    // if (targetSavedCard) {
    //   if (targetSavedCard?.reference) {
    //     openCard(targetSavedCard);
    //   }
    //   return;
    // }

    if (isRailSearchLoading) return;

    const requestFunc = getRequestFuncByStrategy(isAuthenticated ? "authenticated" : "not-authenticated", reference);

    requestFunc?.();
  };

  const handleSearch = (reference: string) => {
    const { isValid, errorMessage } = validatorMeta.current;

    if (isValid) {
      if (isReferenceEqualToOpenedCard(reference)) return;
      setErrorMessage("");
      performRequest(reference);
      clearViewHistory();
      return;
    }
    setErrorMessage(errorMessage);
  };

  return {
    handleSearch,
  };
};
